import { useSignUp } from '@clerk/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { EnvelopeSimple, User } from '@phosphor-icons/react';
import {
  Button,
  Checkbox,
  Divider,
  Field,
  Link,
  Stack,
  TextInput,
  toast,
} from '@reelplicate/ui';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router';
import type { z } from 'zod';

import { LEGAL_LINKS, marketingUrl } from '@/lib/marketing';

import { AuthBody, AuthCard, AuthFooter, AuthHeader } from './auth-card';
import { AuthToast } from './auth-toast';
import { describeClerkError } from './clerk-errors';
import { CodeField } from './code-field';
import { OAuthButtons } from './oauth-buttons';
import { PasswordInput } from './password-input';
import { useRedirectTarget, useWithRedirect } from './redirect-target';
import { EmailCodeSchema, SignUpSchema, type SignUpValues } from './schemas';
import { BackToSignInButton, MailShortcuts } from './status-pages';

/** Clerk's request params, mapped onto this form's fields. */
const FIELD_FOR_PARAM: Record<string, keyof SignUpValues> = {
  email_address: 'email',
  password: 'password',
  first_name: 'name',
  last_name: 'name',
};

/**
 * Figma "Auth B — Sign up" (214:23005). `?email=` pre-fills the email (an
 * invite link sends the invited address); `?redirect_url=` rides along to
 * the confirm screen and is where the new account lands.
 */
export function SignUpPage() {
  const navigate = useNavigate();
  const withRedirectUrl = useWithRedirect();
  const email = new URLSearchParams(useLocation().search).get('email') ?? '';
  const { signUp } = useSignUp();
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<SignUpValues>({
    resolver: zodResolver(SignUpSchema),
    defaultValues: { name: '', email, password: '', confirm: '', terms: false },
  });

  const onSubmit = form.handleSubmit(async ({ name, email, password }) => {
    setFailure(null);
    const [firstName, ...rest] = name.trim().split(/\s+/);
    const { error } = await signUp.password({
      emailAddress: email,
      password,
      firstName,
      lastName: rest.join(' ') || undefined,
    });
    if (!error) {
      const sent = await signUp.verifications.sendEmailCode();
      if (!sent.error) {
        await navigate(withRedirectUrl('/sign-up/confirm-email'));
        return;
      }
      setFailure(describeClerkError(sent.error).message);
      return;
    }
    const { message, param } = describeClerkError(error);
    const field = param ? FIELD_FOR_PARAM[param] : undefined;
    if (field) form.setError(field, { message }, { shouldFocus: true });
    else setFailure(message);
  });

  return (
    <>
      {failure && (
        <AuthToast
          title="Couldn’t create your account"
          description={failure}
          onDismiss={() => setFailure(null)}
        />
      )}
      <AuthCard>
        <AuthHeader
          title="Create your account"
          subtitle="Start turning references into repeatable content"
        />
        <form onSubmit={onSubmit} noValidate>
          <AuthBody>
            <OAuthButtons action="Sign up" />
            <Divider tone="subtle" label="or continue with email" />
            <Controller
              name="name"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field label="Full name" error={fieldState.error?.message}>
                  <TextInput
                    {...field}
                    autoComplete="name"
                    placeholder="Your full name"
                    leadingIcon={User}
                  />
                </Field>
              )}
            />
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field label="Email" error={fieldState.error?.message}>
                  <TextInput
                    {...field}
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    leadingIcon={EnvelopeSimple}
                  />
                </Field>
              )}
            />
            {/* Figma "Passwords": side by side from tablet up. */}
            <Stack gap={3} className="tablet:flex-row tablet:items-start">
              <Controller
                name="password"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Password"
                    error={fieldState.error?.message}
                    className="flex-1"
                  >
                    <PasswordInput
                      {...field}
                      reveal
                      autoComplete="new-password"
                      placeholder="Create a password"
                    />
                  </Field>
                )}
              />
              <Controller
                name="confirm"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Confirm password"
                    error={fieldState.error?.message}
                    className="flex-1"
                  >
                    <PasswordInput
                      {...field}
                      autoComplete="new-password"
                      placeholder="Repeat password"
                    />
                  </Field>
                )}
              />
            </Stack>
            <Controller
              name="terms"
              control={form.control}
              render={({ field, fieldState }) => (
                <Stack gap={1.5}>
                  <Checkbox
                    label={
                      <>
                        I agree to the{' '}
                        <LegalLink path={LEGAL_LINKS.terms}>Terms</LegalLink> and{' '}
                        <LegalLink path={LEGAL_LINKS.privacy}>Privacy Policy</LegalLink>
                      </>
                    }
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    aria-invalid={fieldState.error ? true : undefined}
                  />
                  {fieldState.error && (
                    <p role="alert" className="text-body-sm text-status-danger-fg">
                      {fieldState.error.message}
                    </p>
                  )}
                </Stack>
              )}
            />
            {/* Clerk mounts its bot-protection challenge here when it needs one. */}
            <div id="clerk-captcha" />
            <Button
              type="submit"
              size="lg"
              className="w-full"
              loading={form.formState.isSubmitting}
            >
              Create account
            </Button>
          </AuthBody>
        </form>
        <AuthFooter>
          Already have an account?
          <Link asChild>
            <RouterLink to={withRedirectUrl('/sign-in')}>Sign in</RouterLink>
          </Link>
        </AuthFooter>
      </AuthCard>
    </>
  );
}

type EmailCodeValues = z.infer<typeof EmailCodeSchema>;

/**
 * Figma "Auth B — Sign up — confirm email" (214:23111), with the six-digit
 * code Clerk emails instead of a link.
 */
export function ConfirmEmailPage() {
  const navigate = useNavigate();
  const redirectTo = useRedirectTarget();
  const withRedirectUrl = useWithRedirect();
  const { signUp } = useSignUp();
  const form = useForm<EmailCodeValues>({
    resolver: zodResolver(EmailCodeSchema),
    defaultValues: { code: '' },
  });

  const onSubmit = form.handleSubmit(async ({ code }) => {
    const { error } = await signUp.verifications.verifyEmailCode({ code });
    if (error) {
      form.setError('code', { message: describeClerkError(error).message });
      return;
    }
    if (signUp.status !== 'complete') {
      form.setError('code', { message: 'Your account needs more details to finish.' });
      return;
    }
    await signUp.finalize({ navigate: () => navigate(redirectTo, { replace: true }) });
  });

  async function resend() {
    const { error } = await signUp.verifications.sendEmailCode();
    if (error) toast.error(describeClerkError(error).message);
    else toast.success('New code sent');
  }

  // Reached directly (bookmark, reload after the sign-up expired): start over.
  if (!signUp.emailAddress) return <Navigate to={withRedirectUrl('/sign-up')} replace />;

  return (
    <AuthCard>
      <AuthHeader
        icon={{ icon: EnvelopeSimple }}
        title="Check your email"
        subtitle={`We sent a six-digit code to ${signUp.emailAddress}. Enter it to finish setting up your account.`}
      />
      <form onSubmit={onSubmit} noValidate>
        <AuthBody>
          <Controller
            name="code"
            control={form.control}
            render={({ field, fieldState }) => (
              <CodeField
                value={field.value}
                onChange={field.onChange}
                onComplete={() => void onSubmit()}
                error={fieldState.error?.message}
                autoFocus
              />
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full"
            loading={form.formState.isSubmitting}
          >
            Verify email
          </Button>
          <MailShortcuts />
          <BackToSignInButton variant="ghost" />
        </AuthBody>
      </form>
      <AuthFooter>
        Didn’t get it?
        <Link asChild>
          <button type="button" onClick={() => void resend()}>
            Resend code
          </button>
        </Link>
      </AuthFooter>
    </AuthCard>
  );
}

/**
 * A marketing-site page in a new tab. It sits inside the checkbox's label, so
 * it opens the page itself and cancels the click - otherwise the label would
 * tick the box too.
 */
function LegalLink({ path, children }: { path: string; children: string }) {
  const href = marketingUrl(path);
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="underline"
      onClick={(event) => {
        event.preventDefault();
        window.open(href, '_blank', 'noreferrer');
      }}
    >
      {children}
    </a>
  );
}
